import { useState, useEffect, useCallback } from 'react';
import notify, { MESSAGES } from '../../../utils/notify';
import { api } from '../../../config';
import { getAuthToken, isAuthenticated } from '../../../auth/tokenvalidation';
import { unwrapAwardingList } from '../utils/awardingHelpers';

export const buildAwardingListUrl = ({
  endpoint,
  currentPage,
  rowsPerPage,
  procurementTypeHeader,
  search,
  status,
  sortField,
  sortDirection,
}) => {
  const params = new URLSearchParams();
  params.set('PageNumber', String(currentPage));
  params.set('PageSize', String(rowsPerPage));

  if (procurementTypeHeader) {
    params.set('ProcurementTypeHeader', procurementTypeHeader);
  }

  if (search?.trim()) {
    params.set('Search', search.trim());
  }

  if (status && status !== 'all') {
    params.set('Status', status);
  }

  if (sortField) {
    params.set('SortBy', sortField);
    params.set('IsDescending', sortDirection === 'desc' ? 'true' : 'false');
  }

  return `${api.API_URL}${endpoint}?${params.toString()}`;
};

const useAwardingList = ({
  procurementTypeHeader,
  refreshTrigger = 0,
  enabled = true,
  endpoint = '/awarding',
}) => {
  const [data, setData] = useState({ items: [] });
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortField, setSortField] = useState('procurementNumber');
  const [sortDirection, setSortDirection] = useState('asc');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchData = useCallback(async () => {
    if (!enabled) return;

    const token = getAuthToken();
    if (!token || !isAuthenticated()) {
      notify.error(MESSAGES.authRequired);
      return;
    }

    setIsLoading(true);

    try {
      const url = buildAwardingListUrl({
        endpoint,
        currentPage,
        rowsPerPage,
        procurementTypeHeader,
        search: debouncedSearchText,
        status: statusFilter,
        sortField,
        sortDirection,
      });

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();
      const { items, totalItems } = unwrapAwardingList(result);

      setData({ items });
      setTotalRows(totalItems);
    } catch (error) {
      notify.apiError(error, 'load awarding data');
      setData({ items: [] });
      setTotalRows(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    rowsPerPage,
    debouncedSearchText,
    statusFilter,
    sortField,
    sortDirection,
    procurementTypeHeader,
    enabled,
    endpoint,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshTrigger]);

  const handleSearch = e => setSearchText(e.target.value);

  const handleStatusFilter = value => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handlePageChange = page => setCurrentPage(page);

  const handlePerRowsChange = newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  };

  const handleSort = (column, direction) => {
    const fieldMapping = {
      noProc: 'procurementNumber',
      title: 'titleProcurement',
      procType: 'procurementType',
      description: 'description',
      status: 'status',
    };
    setSortField(fieldMapping[column.id] || column.id || 'procurementNumber');
    setSortDirection(direction);
  };

  const clearFilters = () => {
    setSearchText('');
    setStatusFilter('');
    setSortField('procurementNumber');
    setSortDirection('asc');
    setCurrentPage(1);
  };

  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

  return {
    data,
    totalRows,
    totalPages,
    currentPage,
    rowsPerPage,
    searchText,
    statusFilter,
    isLoading,
    startEntry,
    endEntry,
    handleSearch,
    handleStatusFilter,
    handlePageChange,
    handlePerRowsChange,
    handleSort,
    clearFilters,
    debouncedSearchText,
    sortField,
    sortDirection,
    refresh: fetchData,
  };
};

export default useAwardingList;
